import { IndustryServicePage } from "@/components/pages/IndustryServicePage";
// Phase Q — Industry × Service combo pages (B2B long-tail SEO)
// e.g. /industries/fintech-banking/pdpa-cybersecurity
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  ArrowRight,
  Phone,
  Check,
  Sparkles,
  Target,
  ShieldCheck,
  ChevronRight,
} from "lucide-react";
import { LeadFormModal } from "@/components/LeadFormModal";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { speakableSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import {
  getIndustryBySlug,
  INDUSTRY_SOLUTIONS,
  type IndustrySolution,
} from "@/data/industry-solutions";
import type { ServiceId } from "@/data/services";
import type { ServiceHeadMeta } from "@/data/services-head.gen";

const SITE = "https://nyclanguageinstitute.com";


export const Route = createFileRoute("/industries/$slug/$service/")({
  loader: async ({ params }) => {
    const { SERVICE_HEAD } = await import("@/data/services-head.gen");
    const ind = getIndustryBySlug(params.slug);
    if (!ind) throw notFound();
    if (!(params.service in SERVICE_HEAD)) throw notFound();
    if (!ind.relatedServiceIds.includes(params.service as ServiceId)) throw notFound();
    return { ind, service: SERVICE_HEAD[params.service as ServiceId] };
  },
  head: ({ loaderData }) => {
    const ind = loaderData?.ind;
    const s = loaderData?.service;
    if (!ind || !s)
      return {
        meta: [
          { title: ctaTitle("Industry Service — NYC Legal", "/industries/$slug/$service", "th") },
        ],
      };
    const url = `${SITE}/industries/${ind.slug}/${s.slug}`;
    const title = `${s.nameTh} สำหรับ ${ind.name} | NYC Language Institute`;
    const desc = `บริการ ${s.nameTh} เฉพาะทางสำหรับ ${ind.name} — ${s.shortTh} · ${s.turnaround} · มี Dedicated Account Manager — ส่งภาพเอกสารให้เราตรวจก่อน ฟรี`;
    return {
      meta: [
        { title: ctaTitle(title.slice(0, 60), "/industries/$slug/$service", "th") },
        {
          name: "description",
          content: ctaDesc(desc.slice(0, 158), "/industries/$slug/$service", "th"),
        },
        { name: "keywords", content: `${ind.keywords}, ${s.keywords.slice(0, 6).join(", ")}` },
        { property: "og:title", content: ctaTitle(title, "/industries/$slug/$service", "th") },
        { property: "og:description", content: ctaDesc(desc, "/industries/$slug/$service", "th") },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "th_TH" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/industries/${ind.slug}/${s.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `${s.nameEn} for ${ind.nameEn}`,
              description: desc,
              url,
              provider: { "@id": `${SITE}#organization` },
              areaServed: { "@type": "Country", name: "Thailand" },
              audience: { "@type": "BusinessAudience", audienceType: ind.audience },
              offers: {
                "@type": "Offer",
                priceCurrency: "THB",
                price: s.priceFrom,
                availability: "https://schema.org/InStock",
              },
            }),
          },
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Industry Hub",
                  item: `${SITE}/industries`,
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: ind.name,
                  item: `${SITE}/industries/${ind.slug}`,
                },
                { "@type": "ListItem", position: 4, name: s.nameTh, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: IndustryServicePage,
  notFoundComponent: () => (
    <main className="grid min-h-screen place-items-center bg-navy-deep p-6 text-white">
      <div className="text-center">
        <h1 className="text-3xl font-extrabold">ไม่พบบริการสำหรับอุตสาหกรรมนี้</h1>
        <Link
          to="/industries"
          className="mt-4 inline-block rounded-lg bg-amber-500 px-4 py-2 font-semibold text-navy-deep"
        >
          กลับ Industry Hub
        </Link>
      </div>
    </main>
  ),
});

// Export combo paths for sitemap consumption
export const INDUSTRY_SERVICE_COMBOS: { industry: string; service: ServiceId }[] =
  INDUSTRY_SOLUTIONS.flatMap((i) =>
    i.relatedServiceIds.map((sid) => ({ industry: i.slug, service: sid })),
  );
